namespace $.$$ {

	const SCRIPT_SRC = ( () => {
		if( typeof document === 'undefined' ) return ''
		const s = document.querySelector( 'script[src$="web.js"]' ) as HTMLScriptElement | null
		return s?.src ?? ''
	} )()

	const bases = [ 'slate', 'stone', 'zinc', 'gray' ] as const
	const themes = [ 'sky', 'rose', 'violet', 'emerald', 'amber' ] as const
	const charts = [ 'blue', 'green', 'red', 'yellow', 'purple' ] as const
	const fonts = [ 'inter', 'manrope', 'dm-sans', 'eb-garamond' ] as const
	const radii = [ 'none', 'small', 'medium', 'large' ] as const
	const lights_list = [ 'dark', 'light' ] as const

	type Style = {
		name: string
		base: typeof bases[ number ]
		theme: typeof themes[ number ]
		chart: typeof charts[ number ]
		font: typeof fonts[ number ]
		radius: typeof radii[ number ]
	}
	const styles: Style[] = [
		{ name: 'Default', base: 'slate', theme: 'sky', chart: 'blue', font: 'inter', radius: 'medium' },
		{ name: 'Maia', base: 'stone', theme: 'amber', chart: 'yellow', font: 'eb-garamond', radius: 'large' },
		{ name: 'Mira', base: 'zinc', theme: 'violet', chart: 'purple', font: 'dm-sans', radius: 'small' },
		{ name: 'Sage', base: 'gray', theme: 'emerald', chart: 'green', font: 'manrope', radius: 'medium' },
	]


	const ZIP_FILES = [
		'theme.css',
		'tokens/tokens.ts',
		'div/div.view.tree',
		'div/div.view.css.ts',
		'button/button.view.tree',
		'button/button.view.css.ts',
		'button/button.view.css',
		'card/card.view.tree',
		'card/card.view.css.ts',
		'field/field.view.tree',
		'field/field.view.css.ts',
		'badge/badge.view.tree',
		'badge/badge.view.css.ts',
		'badge/badge.view.css',
		'alert/alert.view.tree',
		'alert/alert.view.css.ts',
		'menu/menu.view.tree',
		'menu/menu.view.css.ts',
		'select/select.view.tree',
		'select/select.view.css.ts',
		'select/select.view.css',
		'tabs/tabs.view.tree',
		'tabs/tabs.view.css.ts',
		'tabs/tabs.view.css',
		'chart/chart.view.tree',
		'chart/chart.view.css.ts',
		'avatar/avatar.view.tree',
		'avatar/avatar.view.css.ts',
		'dialog/dialog.view.tree',
		'dialog/dialog.view.css.ts',
		'dialog/dialog.view.css',
		'progress/progress.view.tree',
		'progress/progress.view.css.ts',
		'progress/progress.view.css',
		'skeleton/skeleton.view.tree',
		'skeleton/skeleton.view.css.ts',
		'skeleton/skeleton.view.css',
		'slider/slider.view.tree',
		'toast/toast.view.tree',
		'toast/toast.view.css.ts',
		'toast/toast.view.css',
		'tooltip/tooltip.view.tree',
		'breadcrumbs/breadcrumbs.view.tree',
		'breadcrumbs/breadcrumbs.view.ts',
		'breadcrumbs/breadcrumbs.view.css.ts',
		'breadcrumbs/breadcrumbs.view.css',
		'router/router.web.ts',
		'router/router.node.ts',
		'router/readme.md',
		'studio/404.html',
	]

	const README = `# BuilderUI kit

Drop this whole \`builderui/\` folder into your bog/ namespace, then wrap your app's root view in $bog_builderui_div and set the preset attributes on it (or on <body>):

  <body
    bog_builderui_lights="dark"
    bog_builderui_base="slate"
    bog_builderui_theme="sky"
    bog_builderui_chart="blue"
    bog_builderui_radius="medium"
    bog_builderui_font_body="inter"
    bog_builderui_font_head="inter"
  >

Components: $bog_builderui_div, $bog_builderui_button, $bog_builderui_card, $bog_builderui_field, $bog_builderui_badge, $bog_builderui_alert, $bog_builderui_menu, $bog_builderui_select, $bog_builderui_tabs, $bog_builderui_chart, $bog_builderui_breadcrumbs (+ $bog_builderui_breadcrumbs_item).

Path routing: call \`$bog_builderui_router.at('/your-app/').activate()\` in your app's static block to get clean URLs (\`/your-app/k=v/k=v\`) instead of \`#!\`. Requires SPA fallback on the server. See \`router/readme.md\`.

Generated by BuilderUI Studio.
`

	export class $bog_builderui_studio extends $.$bog_builderui_studio {

		static {
			$bog_builderui_router.activate()
		}

		@ $mol_mem
		base( next?: typeof bases[ number ] ) {
			return ( $mol_state_arg.value( 'base', next ) || 'slate' ) as typeof bases[ number ]
		}
		@ $mol_mem
		theme( next?: typeof themes[ number ] ) {
			return ( $mol_state_arg.value( 'theme', next ) || 'sky' ) as typeof themes[ number ]
		}
		@ $mol_mem
		chart( next?: typeof charts[ number ] ) {
			return ( $mol_state_arg.value( 'chart', next ) || 'blue' ) as typeof charts[ number ]
		}
		@ $mol_mem
		font_body( next?: typeof fonts[ number ] ) {
			return ( $mol_state_arg.value( 'font_body', next ) || 'inter' ) as typeof fonts[ number ]
		}
		@ $mol_mem
		font_head( next?: typeof fonts[ number ] ) {
			return ( $mol_state_arg.value( 'font_head', next ) || 'inter' ) as typeof fonts[ number ]
		}
		@ $mol_mem
		radius( next?: typeof radii[ number ] ) {
			return ( $mol_state_arg.value( 'radius', next ) || 'medium' ) as typeof radii[ number ]
		}
		@ $mol_mem
		lights( next?: typeof lights_list[ number ] ) {
			return ( $mol_state_arg.value( 'lights', next ) || 'dark' ) as typeof lights_list[ number ]
		}

		shuffle() {
			const pick = < T extends readonly string[] >( list: T ): T[ number ] => list[ Math.floor( Math.random() * list.length ) ]!
			this.base( pick( bases ) )
			this.theme( pick( themes ) )
			this.chart( pick( charts ) )
			this.font_body( pick( fonts ) )
			this.font_head( pick( fonts ) )
			this.radius( pick( radii ) )
		}

		dialog_open() { this.dialog_showed( true ) }
		dialog_close() { this.dialog_showed( false ) }
		dialog_confirm() { this.dialog_showed( false ) }

		share() {
			$mol_wire_async( this ).copy_url()
		}

		async copy_url() {
			await navigator.clipboard.writeText( location.href )
		}

		style_dict() {
			const dict: Record< string, string > = {}
			for( const s of styles ) dict[ s.name ] = s.name
			return dict
		}
		@ $mol_mem
		style_id( next?: string ) {
			if( next !== undefined ) {
				const found = styles.find( s => s.name === next )
				if( found ) {
					this.base( found.base )
					this.theme( found.theme )
					this.chart( found.chart )
					this.font_body( found.font )
					this.font_head( found.font )
					this.radius( found.radius )
				}
				return next
			}
			const i = styles.findIndex( s =>
				s.base === this.base() &&
				s.theme === this.theme() &&
				s.chart === this.chart() &&
				s.font === this.font_body() &&
				s.radius === this.radius()
			)
			return i < 0 ? 'Custom' : styles[ i ].name
		}

		code_preview() {
			return [
				`<body`,
				`  bog_builderui_lights="${ this.lights() }"`,
				`  bog_builderui_base="${ this.base() }"`,
				`  bog_builderui_theme="${ this.theme() }"`,
				`  bog_builderui_chart="${ this.chart() }"`,
				`  bog_builderui_radius="${ this.radius() }"`,
				`  bog_builderui_font_body="${ this.font_body() }"`,
				`  bog_builderui_font_head="${ this.font_head() }"`,
				`>`,
			].join( '\n' )
		}

		chart_visits() { return [ 24, 38, 32, 51, 44, 60, 55 ] }
		chart_signups() { return [ 14, 22, 18, 30, 26, 36, 32 ] }
		chart_logins() { return [ 9, 14, 12, 19, 18, 24, 22 ] }
		chart_labels() { return [ 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun' ] }
		chart_color_visits() { return 'var(--bog_builderui_chart)' }
		chart_color_signups() { return 'var(--bog_builderui_special)' }
		chart_color_logins() { return 'var(--bog_builderui_current)' }

		get_code() {
			this.get_code_showed( true )
		}

		get_code_close() {
			this.get_code_showed( false )
		}

		get_code_confirm() {
			this.get_code_showed( false )
			$mol_wire_async( this ).download_zip()
		}

		async download_zip() {
			const JSZip = ( globalThis as any ).JSZip
			if( !JSZip ) throw new Error( 'JSZip not loaded' )

			const slug = ( s: string ) =>
				s.toLowerCase().replace( /[^a-z0-9_]+/g, '_' ).replace( /^_+|_+$/g, '' )

			const ns = slug( this.namespace() )
			const name = slug( this.app_name() )
			if( !name ) return

			const full = ns ? `${ ns }_${ name }` : name
			const prefix = `${ full }_`

			const rename = ( src: string ) =>
				src
					.replace( /bog_builderui_/g, prefix )
					.replace( /'bog_builderui'/g, `'${ full }'` )

			const base = new URL(
				new URL( SCRIPT_SRC ).pathname.includes( '/studio/-/' ) ? '../../' : 'bog/builderui/',
				SCRIPT_SRC,
			).href

			const zip = new JSZip()
			const root = zip.folder( name )!
			root.file( 'README.md', rename( README ) )
			for( const path of ZIP_FILES ) {
				const res = await fetch( base + path )
				const text = await res.text()
				root.file( path, rename( text ) )
			}
			const blob = await zip.generateAsync( { type: 'blob' } )
			const url = URL.createObjectURL( blob )
			const a = document.createElement( 'a' )
			a.href = url
			a.download = `${ full }.zip`
			document.body.appendChild( a )
			a.click()
			a.remove()
			URL.revokeObjectURL( url )
		}

	}
}
